/**
 * Custom Chat Widget Integration
 * Handles opening the chat widget for buttons with data-chat-widget attribute
 * Also manages widget visibility on mobile screens near footer
 */

class CustomGorgiasChat {
  constructor() {
    this.isHidden = false;
    this.scrollThreshold = 100;
    this.mobileBreakpoint = 749;
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupWidget());
    } else {
      this.setupWidget();
    }
  }

  setupWidget() {
    this.bindEvents();
    this.bindScrollListener();
    this.bindChatStateListener();
  }

  bindEvents() {
    const chatButtons = document.querySelectorAll('[data-chat-widget="true"]');

    chatButtons.forEach(button => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.openChat();
      });
    });
  }

  bindScrollListener() {
    let ticking = false;
    
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    });
  }

  handleScroll() {
    // Only run on screens 749px and smaller
    if (window.innerWidth > this.mobileBreakpoint) {
      if (this.isHidden) {
        this.showWidget();
      }
      return;
    }

    const footer = document.querySelector('.footer-utilities__group-copyright');
    if (!footer) return;

    const footerRect = footer.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    
    // Check if user is within 100px of the footer
    const distanceToFooter = footerRect.top - windowHeight;
    const shouldHide = distanceToFooter <= this.scrollThreshold;

    if (shouldHide && !this.isHidden) {
      this.hideWidget();
    } else if (!shouldHide && this.isHidden) {
      this.showWidget();
    }
  }

  hideWidget() {
    const iframes = this.getGorgiasIframes();
    iframes.forEach(iframe => {
      if (iframe) {
        iframe.style.display = 'none';
      }
    });
    this.isHidden = true;
  }

  showWidget() {
    const iframes = this.getGorgiasIframes();
    iframes.forEach(iframe => {
      if (iframe) {
        iframe.style.display = '';
      }
    });
    this.isHidden = false;
  }

  getGorgiasIframes() {
    return [
      document.querySelector('#chat-window'),
      document.querySelector('iframe[src*="gorgias"]'),
      document.querySelector('[id*="gorgias"]')
    ].filter(Boolean);
  }

  bindChatStateListener() {
    const syncChatState = () => {
      // Optional chaining (?.) safely checks if GorgiasChat and isOpen exist
      const isOpen = window.GorgiasChat?.isOpen?.();
      if (typeof isOpen === 'boolean') document.body.classList.toggle('gorgias-chat-open', isOpen);
    };
  
    const setupListeners = () => {
      const chat = window.GorgiasChat;
      if (!chat?.on) return;
  
      // Standard events
      chat.on('widget:opened', () => document.body.classList.add('gorgias-chat-open'));
      chat.on('widget:closed', () => document.body.classList.remove('gorgias-chat-open'));
      
      // Optional 'ready' event
      try { chat.on('ready', syncChatState); } catch (e) {}
  
      // Initial sync and fallback checks
      syncChatState();
      [1000, 2500, 5000].forEach(ms => setTimeout(syncChatState, ms));
    };
  
    // Polling for the SDK
    const poll = setInterval(() => {
      if (!window.GorgiasChat) return;
      clearInterval(poll);
  
      // Ensure we handle the init promise if it exists
      const initResult = typeof window.GorgiasChat.init === 'function' ? window.GorgiasChat.init() : null;
  
      if (initResult instanceof Promise) {
        initResult.then(setupListeners).catch(setupListeners);
      } else {
        setupListeners();
      }
    }, 500);
  
    // Safety timeout to stop polling after 30s
    setTimeout(() => clearInterval(poll), 30000);
  }

  openChat() {
    if (window.GorgiasChat) {
      window.GorgiasChat.open();
    } else {
      console.error('Gorgias chat widget not available');
    }
  }
}

new CustomGorgiasChat();
